import React, { useCallback, useEffect, useState } from "react";
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc } from "firebase/firestore";
import { db } from "../firebase";
import { UserAuth, callServer } from "../context/AuthContext";
import Carousel from "./Components/Carousel";
import HeaderComp from "./Components/HeaderComp";
import Footer from "./Components/Footer";
import { formatCourseDate } from "../utils/courseTime";
import { syncPublicProfile, removePublicProfile } from "../utils/publicProfile";

const ROLE_LABELS = { 0: "Admin", 1: "Professeur", 2: "Élève" };

const AdminPage = () => {
  const { user: currentUser, deleteCourse, refundCourseStudents } = UserAuth();

  const [users, setUsers] = useState([]);
  const [courses, setCourses] = useState([]);
  const [slides, setSlides] = useState([]);
  const [message, setMessage] = useState(null);

  const [editUserId, setEditUserId] = useState(null);
  const [editFormData, setEditFormData] = useState({ role: 2, balance: 0 });
  const [confirm, setConfirm] = useState(null); // { type, id }

  const [paypal, setPaypal] = useState({ clientId: "", secret: "", mode: "live" });
  const [paypalBusy, setPaypalBusy] = useState(false);
  const [cleaning, setCleaning] = useState(false);

  const [text, setText] = useState("");
  const [imageUrl, setImageUrl] = useState("");

  const notify = (type, msg) => {
    setMessage({ type, text: msg });
    setTimeout(() => setMessage(null), 4000);
  };

  // ─── Chargements ───
  const getUsers = useCallback(async () => {
    const snap = await getDocs(collection(db, "users"));
    const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    setUsers(list);
    // Fiches publiques des profs (crée celles des profs inscrits avant leur mise en place)
    Promise.all(list.map((u) => syncPublicProfile(u.id, u))).catch((e) =>
      console.warn("Synchronisation des fiches publiques :", e.code || e.message)
    );
  }, []);

  const getCourses = useCallback(async () => {
    const snap = await getDocs(collection(db, "courses"));
    setCourses(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
  }, []);

  const getSlides = useCallback(async () => {
    const snap = await getDocs(collection(db, "slides"));
    setSlides(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
  }, []);

  useEffect(() => {
    Promise.all([getUsers(), getCourses(), getSlides()]).catch((e) => {
      console.error(e);
      notify("error", "Erreur de chargement (vérifiez les règles Firestore).");
    });
  }, [getUsers, getCourses, getSlides]);

  // ─── Utilisateurs ───
  const handleEditClick = (u) => {
    setEditUserId(u.id);
    setEditFormData({ role: u.role ?? 2, balance: Number(u.balance) || 0 });
  };

  const handleSaveClick = async () => {
    const updated = { role: Number(editFormData.role), balance: Math.round(Number(editFormData.balance) * 100) / 100 };
    if (![0, 1, 2].includes(updated.role) || isNaN(updated.balance) || updated.balance < 0) {
      notify("error", "Rôle (0, 1 ou 2) ou solde invalide.");
      return;
    }
    try {
      const edited0 = users.find((u) => u.id === editUserId);
      if (edited0?.role !== updated.role) await updateDoc(doc(db, "users", editUserId), { role: updated.role });
      // Le solde passe par le serveur (correction notée dans le journal)
      if ((Number(edited0?.balance) || 0) !== updated.balance) {
        await callServer("adminSetBalance", { uid: editUserId, balance: updated.balance });
      }
      const edited = users.find((u) => u.id === editUserId);
      syncPublicProfile(editUserId, { ...edited, ...updated }).catch(() => {});
      setUsers((prev) => prev.map((u) => (u.id === editUserId ? { ...u, ...updated } : u)));
      setEditUserId(null);
      notify("success", "Utilisateur mis à jour.");
    } catch (e) {
      console.error(e);
      notify("error", `Mise à jour impossible : ${e.message}`);
    }
  };

  const deleteUser = async (id) => {
    if (id === currentUser?.uid) {
      notify("error", "Vous ne pouvez pas supprimer votre propre compte ici.");
      return;
    }
    try {
      await deleteDoc(doc(db, "users", id));
      await removePublicProfile(id).catch(() => {});
      setUsers((prev) => prev.filter((u) => u.id !== id));
      notify("success", "Utilisateur supprimé.");
    } catch (e) {
      console.error(e);
      notify("error", "Suppression impossible.");
    } finally {
      setConfirm(null);
    }
  };

  // ─── Cours ───
  const handleRefund = async (course) => {
    try {
      const n = await refundCourseStudents(course.id);
      await Promise.all([getCourses(), getUsers()]);
      notify("success", n ? `${n} élève(s) remboursé(s) pour « ${course.name} ».` : "Aucun élève à rembourser.");
    } catch (e) {
      console.error(e);
      notify("error", `Le remboursement a échoué : ${e.message}`);
    } finally {
      setConfirm(null);
    }
  };

  const handleDeleteCourse = async (course) => {
    try {
      await deleteCourse(course.id);
      await Promise.all([getCourses(), getUsers()]);
      notify("success", `Cours « ${course.name} » supprimé (élèves remboursés).`);
    } catch (e) {
      console.error(e);
      notify("error", `La suppression du cours a échoué : ${e.message}`);
    } finally {
      setConfirm(null);
    }
  };

  // ─── PayPal (clés gardées par le serveur, jamais relisibles) ───
  const savePaypal = async (e) => {
    e.preventDefault();
    setPaypalBusy(true);
    try {
      const r = await callServer("adminSetPaypalConfig", paypal);
      setPaypal({ clientId: "", secret: "", mode: r.mode });
      notify("success", `Clés PayPal vérifiées et enregistrées (mode ${r.mode === "live" ? "réel" : "test"}).`);
    } catch (err) {
      notify("error", `Clés PayPal refusées : ${err.message}`);
    } finally {
      setPaypalBusy(false);
    }
  };

  // ─── Nettoyage des anciennes données (e-mails des profs dans les cours) ───
  const cleanup = async () => {
    setCleaning(true);
    try {
      const r = await callServer("adminCleanup", {});
      await getCourses();
      notify("success", `Nettoyage terminé : ${r.courses} cours et ${r.enrollments} inscription(s) mis à jour.`);
    } catch (err) {
      notify("error", `Nettoyage impossible : ${err.message}`);
    } finally {
      setCleaning(false);
    }
  };

  const profName = (c) => {
    const u = users.find((x) => x.id === c.ownerId);
    return u ? `${u.firstName || ""} ${u.lastName || ""}`.trim() || u.email : c.ownerId || "—";
  };

  // ─── Annonces (slides) ───
  const addSlide = async () => {
    if (!text.trim() && !imageUrl.trim()) return;
    await addDoc(collection(db, "slides"), { text: text.trim(), imageUrl: imageUrl.trim() });
    setText("");
    setImageUrl("");
    getSlides();
  };

  const updateSlideText = async (id, newText) => {
    await updateDoc(doc(db, "slides", id), { text: newText });
    notify("success", "Annonce mise à jour.");
    getSlides();
  };

  const deleteSlide = async (id) => {
    await deleteDoc(doc(db, "slides", id));
    getSlides();
  };

  const isConfirming = (type, id) => confirm?.type === type && confirm?.id === id;

  const btn = "px-3 py-1.5 rounded-md text-white text-xs font-semibold transition-colors";

  return (
    <div className="min-h-screen bg-creme flex flex-col">
      <HeaderComp />
      <div className="max-w-6xl mx-auto w-full px-4 py-10 flex-1 space-y-12">
        <h1 className="text-4xl font-extrabold text-stone-900 text-center">Administration</h1>

        {message && (
          <div className={`px-4 py-3 rounded-lg text-sm ${message.type === "success" ? "bg-green-50 border border-green-200 text-green-700" : "bg-red-50 border border-red-200 text-red-700"}`}>
            {message.text}
          </div>
        )}

        {/* ─── Utilisateurs ─── */}
        <section>
          <h2 className="text-2xl font-bold mb-2">Utilisateurs</h2>
          <p className="text-xs text-stone-500 mb-4">Rôles : 0 = admin, 1 = professeur, 2 = élève</p>
          <div className="overflow-x-auto bg-white rounded-xl shadow">
            <table className="w-full table-auto text-sm">
              <thead className="bg-stone-100 text-left">
                <tr>
                  <th className="px-4 py-2">Nom</th>
                  <th className="px-4 py-2">Email</th>
                  <th className="px-4 py-2">Rôle</th>
                  <th className="px-4 py-2">Solde</th>
                  <th className="px-4 py-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id} className="border-t">
                    <td className="px-4 py-2">{u.firstName || u.lastName ? `${u.firstName || ""} ${u.lastName || ""}` : u.displayName}</td>
                    <td className="px-4 py-2">{u.email}</td>
                    <td className="px-4 py-2">
                      {editUserId === u.id ? (
                        <select
                          value={editFormData.role}
                          onChange={(e) => setEditFormData({ ...editFormData, role: Number(e.target.value) })}
                          className="p-1 border rounded"
                        >
                          <option value={0}>0 – Admin</option>
                          <option value={1}>1 – Professeur</option>
                          <option value={2}>2 – Élève</option>
                        </select>
                      ) : (
                        ROLE_LABELS[u.role] ?? u.role
                      )}
                    </td>
                    <td className="px-4 py-2">
                      {editUserId === u.id ? (
                        <input
                          type="number"
                          min={0}
                          value={editFormData.balance}
                          onChange={(e) => setEditFormData({ ...editFormData, balance: e.target.value })}
                          className="p-1 border rounded w-24"
                        />
                      ) : (
                        Number(u.balance) || 0
                      )}
                    </td>
                    <td className="px-4 py-2 whitespace-nowrap space-x-2">
                      {editUserId === u.id ? (
                        <>
                          <button onClick={handleSaveClick} className={`${btn} bg-blue-500 hover:bg-blue-600`}>Enregistrer</button>
                          <button onClick={() => setEditUserId(null)} className={`${btn} bg-stone-400 hover:bg-stone-500`}>Annuler</button>
                        </>
                      ) : (
                        <button onClick={() => handleEditClick(u)} className={`${btn} bg-green-500 hover:bg-green-600`}>Modifier</button>
                      )}
                      {isConfirming("user", u.id) ? (
                        <button onClick={() => deleteUser(u.id)} className={`${btn} bg-red-700`}>Confirmer ?</button>
                      ) : (
                        <button onClick={() => setConfirm({ type: "user", id: u.id })} className={`${btn} bg-red-500 hover:bg-red-600`}>Supprimer</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* ─── Cours ─── */}
        <section>
          <h2 className="text-2xl font-bold mb-1">Cours</h2>
          <p className="text-sm text-stone-500 mb-4">
            Commissions Tatie Soso encaissées :{" "}
            <strong>{Math.round(courses.reduce((s, c) => s + (Number(c.payoutCommission) || 0), 0) * 100) / 100} pièces</strong>
          </p>
          <div className="overflow-x-auto bg-white rounded-xl shadow">
            <table className="w-full table-auto text-sm">
              <thead className="bg-stone-100 text-left">
                <tr>
                  <th className="px-4 py-2">Cours</th>
                  <th className="px-4 py-2">Professeur</th>
                  <th className="px-4 py-2">Élèves</th>
                  <th className="px-4 py-2">Prix</th>
                  <th className="px-4 py-2">Date</th>
                  <th className="px-4 py-2">Prof payé</th>
                  <th className="px-4 py-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {courses.map((c) => (
                  <tr key={c.id} className="border-t">
                    <td className="px-4 py-2">{c.name}</td>
                    <td className="px-4 py-2">{profName(c)}</td>
                    <td className="px-4 py-2">{c.students || 0}/{c.maxStudents}</td>
                    <td className="px-4 py-2">{c.price}</td>
                    <td className="px-4 py-2">{formatCourseDate(c)}</td>
                    <td className="px-4 py-2">
                      {c.paidOut ? `✓ ${c.payoutProf} (comm. ${c.payoutCommission})` : "—"}
                    </td>
                    <td className="px-4 py-2 whitespace-nowrap space-x-2">
                      {isConfirming("refund", c.id) ? (
                        <button onClick={() => handleRefund(c)} className={`${btn} bg-yellow-700`}>Confirmer ?</button>
                      ) : (
                        <button onClick={() => setConfirm({ type: "refund", id: c.id })} className={`${btn} bg-yellow-500 hover:bg-yellow-600`}>Rembourser</button>
                      )}
                      {isConfirming("course", c.id) ? (
                        <button onClick={() => handleDeleteCourse(c)} className={`${btn} bg-red-700`}>Confirmer ?</button>
                      ) : (
                        <button onClick={() => setConfirm({ type: "course", id: c.id })} className={`${btn} bg-red-500 hover:bg-red-600`}>Supprimer</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* ─── Paiements PayPal ─── */}
        <section>
          <h2 className="text-2xl font-bold mb-1">Paiements PayPal</h2>
          <p className="text-sm text-stone-600 mb-4">
            Pour que les dépôts soient vérifiés par le serveur, colle ici les clés de ton application PayPal
            (developer.paypal.com → Apps &amp; Credentials). Le « Client ID » doit être le même que celui du fichier .env du site.
            La clé secrète est gardée par le serveur : personne ne peut la relire, même toi — pour la changer, recolle-la.
          </p>
          <form onSubmit={savePaypal} className="bg-white rounded-xl shadow p-4 grid gap-3 md:grid-cols-[1fr_1fr_auto_auto] items-end">
            <label className="text-xs font-semibold text-stone-600">Client ID
              <input value={paypal.clientId} onChange={(e) => setPaypal({ ...paypal, clientId: e.target.value })} className="mt-1 w-full border rounded p-2 text-sm font-normal" autoComplete="off" />
            </label>
            <label className="text-xs font-semibold text-stone-600">Clé secrète (Secret)
              <input type="password" value={paypal.secret} onChange={(e) => setPaypal({ ...paypal, secret: e.target.value })} className="mt-1 w-full border rounded p-2 text-sm font-normal" autoComplete="new-password" />
            </label>
            <label className="text-xs font-semibold text-stone-600">Mode
              <select value={paypal.mode} onChange={(e) => setPaypal({ ...paypal, mode: e.target.value })} className="mt-1 block border rounded p-2 text-sm font-normal">
                <option value="live">Réel (Live)</option>
                <option value="sandbox">Test (Sandbox)</option>
              </select>
            </label>
            <button disabled={paypalBusy || !paypal.clientId.trim() || !paypal.secret.trim()} className={`${btn} bg-prune hover:bg-prune-dark py-2.5 disabled:opacity-50`}>
              {paypalBusy ? "Vérification…" : "Vérifier et enregistrer"}
            </button>
          </form>
        </section>

        {/* ─── Maintenance ─── */}
        <section>
          <h2 className="text-2xl font-bold mb-1">Nettoyage des anciennes données</h2>
          <p className="text-sm text-stone-600 mb-3">
            Retire l'e-mail des profs des fiches de cours publiques (anciens cours) et complète les anciennes inscriptions.
            À faire une fois après la mise en ligne de cette version.
          </p>
          <button onClick={cleanup} disabled={cleaning} className={`${btn} bg-sauge hover:bg-sauge-dark py-2 disabled:opacity-50`}>
            {cleaning ? "Nettoyage…" : "Lancer le nettoyage"}
          </button>
        </section>

        {/* ─── Annonces ─── */}
        <section>
          <h2 className="text-2xl font-bold mb-4">Annonces (bandeau de la page d'accueil)</h2>
          {slides.map((slide, index) => (
            <div key={slide.id} className="flex items-center gap-2 my-2">
              <input
                type="text"
                value={slide.text || ""}
                onChange={(e) => {
                  const next = [...slides];
                  next[index] = { ...next[index], text: e.target.value };
                  setSlides(next);
                }}
                className="border rounded p-2 flex-1"
              />
              <button onClick={() => updateSlideText(slide.id, slide.text)} className={`${btn} bg-blue-500 hover:bg-blue-600`}>Mettre à jour</button>
              <button onClick={() => deleteSlide(slide.id)} className={`${btn} bg-red-500 hover:bg-red-600`}>Supprimer</button>
            </div>
          ))}
          <div className="flex flex-wrap gap-2 mt-4">
            <input type="text" value={text} onChange={(e) => setText(e.target.value)} className="border rounded p-2 flex-1 min-w-[200px]" placeholder="Texte de l'annonce" />
            <input type="text" value={imageUrl} onChange={(e) => setImageUrl(e.target.value)} className="border rounded p-2 flex-1 min-w-[200px]" placeholder="URL d'image (optionnel)" />
            <button onClick={addSlide} className={`${btn} bg-green-500 hover:bg-green-600`}>Ajouter</button>
          </div>
          <div className="bg-ardoise rounded-xl mt-6">
            <Carousel />
          </div>
        </section>
      </div>
      <Footer />
    </div>
  );
};

export default AdminPage;
